import { apiCall } from "../../utils/api";
import { notification } from "../home/action";
import TYPE from "../../constants/type";
import { API_URLS } from "../../constants/api";
import { PREFIX } from "../../constants/enums";

// -------------------------------------------------------------------------
// Internal helpers
// -------------------------------------------------------------------------

// Tất cả action đi theo 3 lifecycle type: LOADING → SUCCESS | FAILURE.
// Hầu hết action ngoài create/update/filter sẽ phát LOADING chung
// (STUDENT_RESEARCH_ACTION_*) — reducer chỉ quan tâm kết quả & patch local list.
const dispatchLifecycle = (
  dispatch,
  { loadingType, successType, failureType, payload = null, extras = {} }
) => {
  dispatch({
    type: loadingType,
    meta: { prefix: [PREFIX.STUDENT_RESEARCH, PREFIX.API_CALLING] },
  });
  return {
    succeed: (data) =>
      dispatch({
        type: successType,
        payload: data ?? payload,
        ...extras,
        meta: { prefix: [PREFIX.STUDENT_RESEARCH, PREFIX.API_SUCCESS] },
      }),
    fail: () =>
      dispatch({
        type: failureType,
        meta: { prefix: [PREFIX.STUDENT_RESEARCH, PREFIX.API_FAILURE] },
      }),
  };
};

const notify = (dispatch, type, message) =>
  dispatch(notification({ type, message }));

// -------------------------------------------------------------------------
// Queries
// -------------------------------------------------------------------------

export const getAllStudentResearch = () => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.getAllStudentResearch();
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.GET_ALL_STUDENT_RESEARCH_LOADING,
    successType: TYPE.STUDENT_RESEARCH.GET_ALL_STUDENT_RESEARCH_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.GET_ALL_STUDENT_RESEARCH_FAILURE,
  });
  const { response, error } = await apiCall({ ...api });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
  } else {
    lc.fail();
  }
};

export const filterStudentResearch = (payload) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.filterStudentResearch();
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.FILTER_STUDENT_RESEARCH_LOADING,
    successType: TYPE.STUDENT_RESEARCH.FILTER_STUDENT_RESEARCH_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.FILTER_STUDENT_RESEARCH_FAILURE,
  });
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
  } else {
    lc.fail();
    return false;
  }
};

export const filterStudentResearchV2 = (payload) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.filterStudentResearchV2();
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.FILTER_STUDENT_RESEARCH_LOADING,
    successType: TYPE.STUDENT_RESEARCH.FILTER_STUDENT_RESEARCH_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.FILTER_STUDENT_RESEARCH_FAILURE,
  });
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
  } else {
    lc.fail();
  }
};

// -------------------------------------------------------------------------
// Registration (R2: gộp đăng ký + thành viên)
// -------------------------------------------------------------------------

// createStudentResearch — trả về entity vừa tạo (có id) khi success, hoặc null khi fail.
// Cho phép caller dùng id ngay (ví dụ: tạo xong submit luôn).
export const createStudentResearch = (payload) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.createStudentResearch();
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.CREATE_STUDENT_RESEARCH_LOADING,
    successType: TYPE.STUDENT_RESEARCH.CREATE_STUDENT_RESEARCH_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.CREATE_STUDENT_RESEARCH_FAILURE,
  });
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    const entity = response.data.data;
    lc.succeed(entity);
    notify(dispatch, "success", "Thêm đề tài thành công!");
    return entity;
  }
  lc.fail();
  // Hiển thị message lỗi cụ thể từ BE nếu có (vd: trùng tên, validate fail).
  const serverMsg = error?.response?.data?.message || response?.data?.message;
  notify(
    dispatch,
    "error",
    `Thêm đề tài thất bại! ${serverMsg || ""}`
  );
  return null;
};

export const updateStudentResearch = (id, payload) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.updateStudentResearch(id);
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.UPDATE_STUDENT_RESEARCH_LOADING,
    successType: TYPE.STUDENT_RESEARCH.UPDATE_STUDENT_RESEARCH_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.UPDATE_STUDENT_RESEARCH_FAILURE,
  });
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
    return true;
  }
  lc.fail();
  notify(
    dispatch,
    "error",
    `Cập nhật đề tài thất bại! ${error?.response?.data?.message || ""}`
  );
  return false;
};

export const deleteStudentResearch = (id) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.deleteStudentResearch(id);
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.DELETE_STUDENT_RESEARCH_LOADING,
    successType: TYPE.STUDENT_RESEARCH.DELETE_STUDENT_RESEARCH_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.DELETE_STUDENT_RESEARCH_FAILURE,
    extras: { id },
  });
  const { response, error } = await apiCall({ ...api });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed();
    return true;
  }
  lc.fail();
  return false;
};

// -------------------------------------------------------------------------
// Actions theo state machine mới
// -------------------------------------------------------------------------

// refreshStudentResearch — fetch entity bằng id và dispatch SUCCESS với payload
// đầy đủ. Dùng để đồng bộ status/list sau mỗi action-based call (nhiều endpoint
// BE trả `ok(w,r,nil)` không kèm entity).
const refreshStudentResearch = async (dispatch, id) => {
  try {
    const api = API_URLS.STUDENT_RESEARCH.getStudentResearchById(id);
    const res = await apiCall({ ...api });
    if (!res.error && res.response?.status === 200 && res.response.data.success) {
      dispatch({
        type: TYPE.STUDENT_RESEARCH.UPDATE_STUDENT_RESEARCH_SUCCESS,
        payload: res.response.data.data,
        meta: { prefix: [PREFIX.STUDENT_RESEARCH, PREFIX.API_SUCCESS] },
      });
    }
  } catch (e) {
    // Nuốt lỗi refresh — action chính đã OK, refresh chỉ để đồng bộ UI.
  }
};

// runAction: helper chung cho mọi action action-based theo state machine mới.
// Flow:
//   1. Gọi API chính, dispatch lifecycle (LOADING → SUCCESS/FAILURE).
//   2. Sau SUCCESS, gọi refresh để fetch entity mới từ server → đồng bộ list.
//      Tránh stale UI khi BE không trả kèm entity.
const runAction = async (
  dispatch,
  { api, payload, successMessage, errorMessage, extras = {} }
) => {
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.STUDENT_RESEARCH_ACTION_LOADING,
    successType: TYPE.STUDENT_RESEARCH.STUDENT_RESEARCH_ACTION_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.STUDENT_RESEARCH_ACTION_FAILURE,
    extras,
  });
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
    if (successMessage) notify(dispatch, "success", successMessage);
    if (extras?.id) await refreshStudentResearch(dispatch, extras.id);
    return true;
  }
  lc.fail();
  if (errorMessage) {
    const serverMsg = error?.response?.data?.message || response?.data?.message;
    notify(dispatch, "error", `${errorMessage}${serverMsg ? ` ${serverMsg}` : ""}`);
  }
  return false;
};

export const submitRegistration = (id) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.submitRegistration(id),
    successMessage: "Đã nộp đăng ký!",
    errorMessage: "Nộp đăng ký thất bại!",
    extras: { id },
  });

export const reviewRegistration = (id, action, comment) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.reviewRegistration(id),
    payload: { action, comment },
    successMessage: "Đã cập nhật kết quả duyệt đăng ký!",
    errorMessage: "Duyệt đăng ký thất bại!",
    extras: { id },
  });

// conductStudentResearch — admin có thể truyền `researchCode` để gán/sửa mã
// (refactor 2026-05-21). Bỏ trống → BE auto-gen `SV-{year}-{seq:04d}`.
export const conductStudentResearch = (id, researchCode = "") => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.conductStudentResearch(id),
    payload: { researchCode },
    successMessage: "Đã duyệt thực hiện và cấp mã đề tài!",
    errorMessage: "Duyệt thực hiện thất bại!",
    extras: { id },
  });

// suggestSRResearchCode — gọi BE trả về mã `SVNCKH_<khoa>_{yy}.{seq:02d}` tiếp theo.
// yy lấy theo năm **kết thúc** đề tài (endYear), seq đếm riêng theo từng khoa.
// Dùng cho FE pre-fill ô input ở ConductConfirm form. Promise<string>.
export const suggestSRResearchCode = async (schoolYear, departmentId, endYear) => {
  try {
    const api = API_URLS.STUDENT_RESEARCH.suggestSRResearchCode(
      schoolYear,
      departmentId,
      endYear
    );
    const { response, error } = await apiCall({ ...api });
    if (!error && response?.status === 200 && response.data.success) {
      return response.data.data?.suggestedCode || "";
    }
  } catch (e) {
    // ignore
  }
  return "";
};

export const reviewConduct = (id, action, comment) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.reviewConduct(id),
    payload: { action, comment },
    successMessage: "Đã cập nhật kết quả duyệt thực hiện!",
    errorMessage: "Duyệt thực hiện thất bại!",
    extras: { id },
  });

// Progress report ở bảng con. Refactor 2026-08-26: bỏ đợt 2 — chỉ còn 1 BCTĐ,
// `iter` luôn là PROGRESS_REPORT_ITERATION (=1), giữ trong signature vì route
// BE vẫn nhận {iter} trên URL.
// progressData: {completedWork, evaluation, nextPlan, recommendation} — R5.
export const submitProgressReport = (id, iter, progressData) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.submitProgressReport(id, iter),
    payload: progressData,
    successMessage: "Đã nộp báo cáo tiến độ!",
    errorMessage: "Nộp báo cáo tiến độ thất bại!",
    extras: { id },
  });

export const reviewProgressReport = (id, iter, action, comment) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.reviewProgressReport(id, iter),
    payload: { action, comment },
    successMessage: "Đã cập nhật kết quả duyệt báo cáo tiến độ!",
    errorMessage: "Duyệt báo cáo tiến độ thất bại!",
    extras: { id },
  });

// Alias deprecated cho caller cũ. TODO: xóa khi UI hoàn tất refactor.
export const reviewProgress = reviewProgressReport;

export const requestStop = (id, reason, fileLink) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.requestStop(id),
    payload: { reason, fileLink },
    successMessage: "Đã gửi đơn xin dừng!",
    errorMessage: "Gửi đơn xin dừng thất bại!",
    extras: { id },
  });

export const reviewStop = (id, action, comment) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.reviewStop(id),
    payload: { action, comment },
    successMessage: "Đã cập nhật kết quả duyệt đơn xin dừng!",
    errorMessage: "Duyệt đơn xin dừng thất bại!",
    extras: { id },
  });

export const submitAcceptance = (id, finalFileLink) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.submitAcceptance(id),
    payload: { finalFileLink },
    successMessage: "Đã nộp báo cáo tổng kết!",
    errorMessage: "Nộp báo cáo tổng kết thất bại!",
    extras: { id },
  });

// council: { name, type, chairmanPid, chairmanName, chairmanDegree, ... }
export const assignCouncil = (id, council) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.assignCouncil(id),
    payload: council,
    successMessage: "Đã gán hội đồng!",
    errorMessage: "Gán hội đồng thất bại!",
    extras: { id },
  });

// GradeDepartmentLevel — "Nghiệm thu cấp Khoa" gộp (refactor 2026-07-23):
// chấm điểm + kết luận nghiệm thu + gán giải trong 1 hành động.
// payload: { point, estimateFileLink, comment, action, result, eligible, sidePrizes }
export const gradeDepartmentLevel = (id, payload) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.gradeDepartmentLevel(id),
    payload,
    successMessage: "Đã cập nhật kết quả nghiệm thu cấp Khoa!",
    errorMessage: "Nghiệm thu cấp Khoa thất bại!",
    extras: { id },
  });

// payload: { prize, comment, note, proposeNext }
export const gradeSchoolLevel = (id, payload) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.gradeSchoolLevel(id),
    payload,
    successMessage: "Đã cập nhật kết quả cấp Trường!",
    errorMessage: "Cập nhật kết quả cấp Trường thất bại!",
    extras: { id },
  });

// payload: { prize, comment, note, proposeNext }
export const gradeNationalLevel = (id, payload) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.gradeNationalLevel(id),
    payload,
    successMessage: "Đã cập nhật kết quả cấp Quốc gia!",
    errorMessage: "Cập nhật kết quả cấp Quốc gia thất bại!",
    extras: { id },
  });

// payload: { prize, comment, note }
export const gradeInternationalLevel = (id, payload) => (dispatch) =>
  runAction(dispatch, {
    api: API_URLS.STUDENT_RESEARCH.gradeInternationalLevel(id),
    payload,
    successMessage: "Đã cập nhật kết quả cấp Quốc tế!",
    errorMessage: "Cập nhật kết quả cấp Quốc tế thất bại!",
    extras: { id },
  });

// -------------------------------------------------------------------------
// Student Guidance (giữ nguyên, không thuộc refactor này)
// -------------------------------------------------------------------------

export const insertStudentGuidance = (payload) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.insertStudentGuidance();
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.INSERT_STUDENT_GUIDANCE_LOADING,
    successType: TYPE.STUDENT_RESEARCH.INSERT_STUDENT_GUIDANCE_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.INSERT_STUDENT_GUIDANCE_FAILURE,
  });
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
    return true;
  }
  lc.fail();
  return false;
};

export const updateStudentGuidance = (payload) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.updateStudentGuidance();
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.UPDATE_STUDENT_GUIDANCE_LOADING,
    successType: TYPE.STUDENT_RESEARCH.UPDATE_STUDENT_GUIDANCE_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.UPDATE_STUDENT_GUIDANCE_FAILURE,
  });
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
    return true;
  }
  lc.fail();
  return false;
};

export const deleteStudentGuidance = (id) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.deleteStudentGuidance(id);
  const lc = dispatchLifecycle(dispatch, {
    loadingType: TYPE.STUDENT_RESEARCH.DELETE_STUDENT_GUIDANCE_LOADING,
    successType: TYPE.STUDENT_RESEARCH.DELETE_STUDENT_GUIDANCE_SUCCESS,
    failureType: TYPE.STUDENT_RESEARCH.DELETE_STUDENT_GUIDANCE_FAILURE,
  });
  const { response, error } = await apiCall({ ...api });
  if (!error && response?.status === 200 && response.data.success) {
    lc.succeed(response.data.data);
    return true;
  }
  lc.fail();
  return false;
};

// -------------------------------------------------------------------------
// Cấu hình email ghi chú kết quả các cấp (bật/tắt + mẫu email)
// -------------------------------------------------------------------------

// Trả về { setting, placeholders } hoặc null nếu lỗi.
export const getNoteMailSetting = () => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.getNoteMailSetting();
  const { response, error } = await apiCall({ ...api });
  if (!error && response?.status === 200 && response.data.success) {
    return response.data.data;
  }
  notify(dispatch, "error", "Không tải được cấu hình email ghi chú!");
  return null;
};

// payload: { enabled, subject, content }
export const saveNoteMailSetting = (payload) => async (dispatch) => {
  const api = API_URLS.STUDENT_RESEARCH.saveNoteMailSetting();
  const { response, error } = await apiCall({ ...api, payload });
  if (!error && response?.status === 200 && response.data.success) {
    notify(dispatch, "success", "Đã lưu cấu hình email ghi chú!");
    return true;
  }
  notify(dispatch, "error", error?.response?.data?.message || "Lưu cấu hình email ghi chú thất bại!");
  return false;
};
